import { uniqueJoined } from './headers';
import type { EatonCoverageRow, EatonMissingSpa, EatonPriceRow } from './types';

export interface SpaCoverageSummary {
  spaId: string;
  spaDescription: string;
  regions: string;
  customerCount: number;
}

export function summarizeCoverageBySpa(
  coverage: readonly EatonCoverageRow[],
): Map<string, SpaCoverageSummary> {
  const grouped = new Map<string, EatonCoverageRow[]>();
  for (const row of coverage) {
    const current = grouped.get(row.spaId) ?? [];
    current.push(row);
    grouped.set(row.spaId, current);
  }
  const summaries = new Map<string, SpaCoverageSummary>();
  for (const [spaId, rows] of grouped) {
    const first = rows[0];
    summaries.set(spaId, {
      spaId,
      spaDescription: first?.spaDescription ?? '',
      regions: uniqueJoined(rows.map((row) => row.region)),
      customerCount: rows.length,
    });
  }
  return summaries;
}

/** Stamp each price with how many master-list customers that SPA covers. */
export function attachCoverageToPrices(
  prices: readonly EatonPriceRow[],
  coverageBySpa: ReadonlyMap<string, SpaCoverageSummary>,
): EatonPriceRow[] {
  return prices.map((price) => {
    const summary = coverageBySpa.get(price.spaId);
    if (!summary) return price;
    return {
      ...price,
      spaDescription:
        price.spaDescription === ''
          ? summary.spaDescription
          : price.spaDescription,
      customerCount: summary.customerCount,
      coverageRegions: summary.regions === '' ? price.region : summary.regions,
    };
  });
}

export function missingSpasFromCoverage(
  coverageBySpa: ReadonlyMap<string, SpaCoverageSummary>,
  prices: readonly EatonPriceRow[],
): EatonMissingSpa[] {
  const priced = new Set(prices.map((price) => price.spaId));
  return [...coverageBySpa.values()]
    .filter((summary) => !priced.has(summary.spaId))
    .map((summary) => ({
      spaId: summary.spaId,
      spaDescription: summary.spaDescription,
      regions: summary.regions,
      customerCount: summary.customerCount,
    }));
}
